import PropTypes from "prop-types";
import { useId, useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";

export default function FormPasswordInput({
  id,
  label,
  name,
  value,
  defaultValue,
  onChange,
  placeholder,
  autoComplete = "current-password",
  disabled,
  error,
  className = "",
  inputClassName = "",
}) {
  const fallbackId = useId();
  const inputId = id || name || fallbackId;
  const isControlled = value !== undefined;
  const [visible, setVisible] = useState(false);

  const inputClassNames = [
    "w-full rounded-lg border px-3 py-2.5 pr-10 text-sm outline-none transition ring-brand-600 focus:ring-2",
    error ? "border-red-400 bg-red-50/40" : "border-slate-500 bg-white",
    inputClassName,
  ].join(" ");

  return (
    <div className={className}>
      <label htmlFor={inputId} className="mb-1 block text-xs font-semibold text-black">
        {label}
      </label>
      <div className="relative">
        <input
          id={inputId}
          name={name}
          type={visible ? "text" : "password"}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${inputId}-error` : undefined}
          className={inputClassNames}
          {...(isControlled
            ? { value: value ?? "", onChange }
            : { defaultValue, ...(onChange ? { onChange } : {}) })}
        />
        <button
          type="button"
          disabled={disabled}
          className="absolute inset-y-0 right-0 flex items-center pr-2 text-slate-500 hover:text-slate-800 disabled:pointer-events-none disabled:opacity-40"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? "Hide password" : "Show password"}
        >
          {visible ? <FiEyeOff className="h-5 w-5" aria-hidden /> : <FiEye className="h-5 w-5" aria-hidden />}
        </button>
      </div>
      {error ? (
        <p id={`${inputId}-error`} className="mt-1 text-xs font-medium text-red-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

FormPasswordInput.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  name: PropTypes.string,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  autoComplete: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
};
